import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { T } from "react-translator-component";
import { Box, Button, Paper, TableContainer, TextField } from "@mui/material";
import CreateIcon from '@mui/icons-material/Create';
import "../../App.css";
import LoadingModal from "../../components/LoadingModal";
import Separator from "../../components/Separator";
import { colors } from "../../common/colors";
import { styles } from "../../pages/styles";
import "../../App.css";
import { isNavigator } from "../util/util";
import { useAppDispatch } from "../../redux/store";
import { setErrorObj } from "../redux/errorReducer";
import AdminPageFrame from "../components/AdminPageFrame";
import DropDown from "../components/DropDown";
import FetchButton from "../components/FetchButton";
import OpinionsHeader from "../components/post/postCard/OpinionsHeader";
import PostCard from "../components/post/PostCard";
import ReadMore from "../components/ReadMore";
import { initialize } from "../redux/createOpinionReducer";
import apiServices from "../services/apiServices";
import { sortOptions, voteOpenOptions } from "../util/posts";

export default function NavigatorPostList() {
  const { admin } = useSelector((state: any) => state.adminAuth);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectVoteOpenOption, setSelectVoteOpenOption] = useState<boolean | null>(null);
  const [selectSortOption, setSelectSortOption] = useState<string>(sortOptions[0].value);
  const [searchTerm, setSearchTerm] = useState<string>("");

  /* API関連 */
  const [posts, setPosts] = useState<any[]>([]);
  const [lastParams, setLastParams] = useState<any>();
  const [pagination, setPagination] = useState<any>({});

  const createParams = (page?: number) => {
    const sortOrder = parseSortOrder()
    const params = {
      term: searchTerm,
      page: page ?? 1,
      sort_type: sortOrder.sortType,
      order: sortOrder.order,
      vote_open: selectVoteOpenOption,
      per_page: 12
    }

    return params
  }

  const getOpinionPosts = async (params: any) => {
    try {
      setIsLoading(true);
      const res = await apiServices.getOpinionPosts(params);
      if (params.page === 1) {
        setPosts(res.posts)
      } else {
        setPosts([...posts, ...res.posts])
      }
      setLastParams(params)
      setPagination(res.pagination)
    } catch (error: any) {
      dispatch(setErrorObj(error))
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    const params = createParams()
    getOpinionPosts(params)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadMore = () => {
    if (!pagination?.next_page || pagination?.next_page < 2) return

    const params = { ...lastParams, page: pagination.next_page}
    getOpinionPosts(params);
  };

  const handleSelectSession = (e: any) => {
    const value = e.target.value
    if (value === "") {
      setSelectVoteOpenOption(null)
    } else {
      setSelectVoteOpenOption(value === "true");
    }
  };

  const handleSort = (e: any) => {
    setSelectSortOption(e.target.value);
  };

  const handleChangeSearchTerm = (value: string) => {
    setSearchTerm(value);
  }

  const parseSortOrder = () => {
    const [ type, direction ] = selectSortOption.split("_");
    const sortType = type || "created"
    const orderType = direction || "desc"

    return {
      sortType: sortType,
      order: orderType,
    }
  }

  const handleClickCreatePost = (suggestionId?: number) => {
    if (!isNavigator(admin)) return

    dispatch(initialize())
    navigate("/mng/create_opinion_post", { state: { suggestionId: suggestionId } });
  }

  const handleClickTag = (tag: string) => {
    handleChangeSearchTerm(tag)
  }

  const handleSearch = async () => {
    const params = createParams()
    await getOpinionPosts(params)
    return false
  }

  return (
    <AdminPageFrame>
      <LoadingModal visible={isLoading} />

      <TableContainer component={Paper} sx={style.searchContainer}>
        <div style={style.searchTitle}>{T("mng_search_area_title")}</div>

        <div style={style.searchInputBox}>
          <TextField
            placeholder={`${T("mng_search_navigator_posts_placeholder")}`}
            value={searchTerm}
            onChange={(e) => handleChangeSearchTerm(e.target.value)}
            style={style.searchInputForm}
          />
        </div>

        <div style={style.selectAndBtnBox}>
          <div style={style.dropDownsBox}>
            <DropDown
              title={T("mng_sort")}
              value={selectSortOption}
              options={sortOptions}
              onChange={handleSort}
              sx={style.w30}
            />

            <DropDown
              title={T("mng_navigator_posts_fetch_vote")}
              value={selectVoteOpenOption !== null ? String(selectVoteOpenOption) : ""}
              options={voteOpenOptions}
              onChange={handleSelectSession}
              sx={style.w30}
            />
          </div>

          <Box sx={style.btncontainer}>
            <FetchButton
              title={T("mng_search")}
              onSubmit={async () => handleSearch()}
            />
          </Box>
        </div>
      </TableContainer>

      <div style={{...styles.communityContainer, marginTop: 20}}>
        <TableContainer component={Paper} sx={{ minWidth: 650, overflowX: "visible", margin: "20px 0" }}>
          <div aria-label="caption table">
            <div style={style.tableHeader}>
              <div style={style.tableTitle}>{T("mng_navigator_posts")}</div>
              {isNavigator(admin) && (
                <Button sx={style.createButton} onClick={() => handleClickCreatePost()}>
                  <CreateIcon />
                  {T("mng_create_new_opinion_post")}
                </Button>
              )}
            </div>
            <Separator color={colors.farm_gray100} />
          </div>

          <div style={style.postList}>
            {posts.map((post: any, index: number) => (
              <PostCard
                post={post}
                header={<OpinionsHeader post={post} />}
                onClickTag={handleClickTag}
                key={index}
              />
            ))}
          </div>

          <ReadMore open={pagination.next_page > 0} sx={style.paginate} onClick={loadMore} />
        </TableContainer>
      </div>
    </AdminPageFrame>
  );
}

const style = {
  searchContainer: {
    marginBottom: "10px",
    padding: "20px",
  },

  searchTitle: {
    padding: "10px 0",
  },

  dropDownsBox: {
    display: "flex",
    margin: "10px 0 0 10px",
    gap: "20px",
    flex: 1
  },

  searchInputForm: {
    width: "100%",
  },

  searchInputBox: {
    margin: "10px 20px"
  },

  selectAndBtnBox: {
    margin: "10px",
    display: "flex"
  },

  w30: {
    width: "30%"
  },

  tableTitle: {
    fontWeight: 700,
    fontSize: 16,
    lineHeight: "120px",
    paddingLeft: 10,
  },

  paginate: {
    flexDirection: "row" as const,
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
    width: "100%"
  },

  tableHeader: {
    display: "flex",
    justifyContent: "space-between",
    padding: "0 20px",
  },

  postList: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: 20,
  },

  btncontainer: {
    padding: 0,
    display: "flex",
    alignItems: "flex-end",
  },

  createButton: {
    height: "fit-content",
    margin: "auto 0",
  },
}